import React, { useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AvatarSize, EditableImage, EditableName, GroupHeaderDescription, GroupHeaderOptions } from '@pkg/components';
import { EditableDescription, GroupHeaderImage } from '@pkg/components/Sections/Groups';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useGroupId, useMembersPanel } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { type SocialGroup } from '@app/gen';

import { Badges } from './Badges';
import { GroupName } from './GroupName';
import { GrowYourPodsPrompt } from './GrowYourPodsPrompt';

import {
    AddMembersWrapper,
    EditableNameWrapper,
    Root,
    StyledActions,
    StyledCta,
    StyledFollowButton,
    StyledFollowerInfo,
    StyledImage,
    StyledInfo,
    StyledMembers,
    StyledName,
} from './styles';
interface GroupHeaderProps extends Pick<SocialGroup, 'description' | 'members'> {
    name?: SocialGroup['name'];
    privacy?: SocialGroup['privacy'];
    image?: string;
    isFollowing?: boolean;
    href?: string;
    editMode?: boolean;
    id?: string;
    isOwner?: boolean;
    followingCount?: number;
    followingCountWeek?: number;
    hasMembership?: boolean;
    onImagePanelOpen?(): void;
}

export const GroupHeader = ({
    name,
    description,
    members,
    privacy,
    image,
    isFollowing,
    href,
    editMode = false,
    id,
    isOwner,
    followingCount,
    followingCountWeek,
    hasMembership,
    onImagePanelOpen,
}: GroupHeaderProps) => {
    const { t: translate } = useTranslation('groups');
    const { tablet, desktop } = useMediaQuery();
    const groupId = useGroupId();
    const { openPanel } = useMembersPanel();

    function handleOpenPanel() {
        openPanel(groupId);
    }

    const [nameFocused, setNameFocused] = useState(false);
    const handleNameFocus = () => setNameFocused(true);
    const handleNameBlur = () => setNameFocused(false);

    const showUnfollowButton = Boolean(isFollowing && !isOwner && !hasMembership && privacy === 'Public');
    const showFollowButton = !(isOwner || hasMembership || privacy === 'Private' || isFollowing);

    const styledCtaMarkup = !nameFocused ? (
        <StyledCta initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <GroupHeaderOptions showUnfollowButton={showUnfollowButton} />
        </StyledCta>
    ) : null;

    const imageMarkup =
        editMode && onImagePanelOpen ? (
            <EditableImage imageUrl={image} size={getImageSize({ tablet, desktop })} onAction={onImagePanelOpen} />
        ) : (
            <GroupHeaderImage image={image} />
        );

    const nameMarkup = href ? (
        <Link to={href}>
            <GroupName name={name} />
        </Link>
    ) : (
        <GroupName name={name} />
    );

    // TODO: We should merge these components and pass `readOnly={!editMode}`.
    const editableNameMarkup = editMode ? (
        <EditableNameWrapper>
            <EditableName name={name} onFocus={handleNameFocus} onBlur={handleNameBlur} />
        </EditableNameWrapper>
    ) : null;

    const descriptionMarkup = editMode ? (
        <EditableDescription description={description} />
    ) : (
        <GroupHeaderDescription description={description} />
    );

    const styledActionsMarkup = tablet ? null : (
        <>
            {showFollowButton && (
                <StyledActions>
                    <StyledFollowButton groupId={id} isFollowing={false} />
                </StyledActions>
            )}
            {isOwner && (
                <StyledActions>
                    <AddMembersWrapper>
                        <Button variant="solid" color="translucent" size="medium" onClick={handleOpenPanel}>
                            {translate('pods_add_members')}
                        </Button>
                        <GrowYourPodsPrompt isMobile handleOpenPanel={handleOpenPanel} />
                    </AddMembersWrapper>
                </StyledActions>
            )}
        </>
    );

    return (
        <Root>
            {styledActionsMarkup}

            <StyledImage>{imageMarkup}</StyledImage>

            <StyledName>
                {editableNameMarkup || nameMarkup}
                <AnimatePresence>{styledCtaMarkup}</AnimatePresence>
            </StyledName>

            <StyledInfo>
                {descriptionMarkup}

                <StyledFollowerInfo
                    podId={id || ''}
                    membersCount={members?.total}
                    followerCount={followingCount}
                    followerCountWeek={followingCountWeek}
                />

                <Badges isFollowing={isFollowing} hasMembership={hasMembership} isOwner={isOwner} privacy={privacy} />

                <StyledMembers groupId={id} members={members} showAddMembers={isOwner && tablet} />

                {showFollowButton && tablet && <StyledFollowButton groupId={id} isFollowing={false} />}
            </StyledInfo>
        </Root>
    );
};

function getImageSize({ tablet, desktop }: { tablet: boolean; desktop: boolean }) {
    if (desktop) {
        return AvatarSize.Medium;
    } else if (tablet) {
        return AvatarSize.Small;
    }

    return AvatarSize.Smaller;
}
